import React, { useState } from 'react';
import { Mail, Check, Loader2 } from 'lucide-react';

/**
 * EmailSubscribeForm — reusable email-only subscribe form
 * Calls /api/email-subscribe with targetType + targetId.
 * Used in FollowButton popover, content gates, and dashboard CTAs.
 *
 * Variants via `variant` prop:
 * - 'inline' (default): horizontal email + button
 * - 'stacked': vertical email input above button (for narrow containers)
 */
const EmailSubscribeForm = ({
  targetType,
  targetId,
  buttonLabel = 'Subscribe',
  variant = 'inline',
  className = '',
  onSuccess,
}) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!email || status === 'loading') return;

    setStatus('loading');
    setMessage('');

    try {
      const res = await fetch('/api/email-subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), targetType, targetId }),
      });
      const data = await res.json();

      if (res.ok) {
        setStatus('success');
        setMessage(data.alreadyVerified ? 'You\'re already subscribed!' : 'Check your email to confirm');
        if (onSuccess) onSuccess(email);
      } else {
        setStatus('error');
        setMessage(data.error || 'Something went wrong');
      }
    } catch {
      setStatus('error');
      setMessage('Network error — please try again');
    }
  };

  if (status === 'success') {
    return (
      <div className={`flex items-center gap-2 justify-center py-2 ${className}`}>
        <Check size={16} className="text-green-600" />
        <span className="text-sm text-green-700 dark:text-green-400 font-medium">{message}</span>
      </div>
    );
  }

  const isStacked = variant === 'stacked';

  return (
    <form
      onSubmit={handleSubmit}
      onClick={(e) => e.stopPropagation()}
      className={`${isStacked ? 'space-y-2' : 'flex gap-2'} ${className}`}
    >
      <input
        type="email"
        value={email}
        onChange={(e) => { setEmail(e.target.value); if (status === 'error') setStatus('idle'); }}
        placeholder="Your email"
        required
        className={`px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-slate-700 dark:border-slate-600 dark:text-white dark:placeholder-slate-400 ${
          isStacked ? 'w-full' : 'flex-1 min-w-0'
        }`}
      />
      <button
        type="submit"
        disabled={status === 'loading'}
        className={`px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-1.5 ${
          isStacked ? 'w-full' : 'flex-shrink-0'
        }`}
      >
        {status === 'loading' ? (
          <Loader2 size={14} className="animate-spin" />
        ) : (
          <Mail size={14} />
        )}
        {buttonLabel}
      </button>
      {status === 'error' && message && (
        <p className={`text-xs text-red-600 dark:text-red-400 ${isStacked ? '' : 'basis-full'}`}>{message}</p>
      )}
    </form>
  );
};

export default EmailSubscribeForm;
